import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Link2, Clock, AlertTriangle, CheckCircle2, Loader2,
  RefreshCw, ChevronLeft, ChevronRight, Filter, X, Search,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

interface KPIs {
  avgMappingTimeHours: number;
  unmapped: number;
  inProgress: number;
  mapped: number;
  overdue: number;
}

interface MappingItem {
  id: string;
  soId: string;
  soNumber: string;
  itemDescription: string;
  status: 'unmapped' | 'in_progress' | 'mapped';
  delayHours: number;
  poId: string | null;
  poNumber: string | null;
  confirmedAt: string | null;
}

interface PO {
  id: string;
  number: string;
  status: string;
  vendor?: { name: string } | null;
}

const STATUS_BADGE: Record<string, string> = {
  unmapped: 'bg-red-50 text-red-700',
  in_progress: 'bg-yellow-50 text-yellow-700',
  mapped: 'bg-green-50 text-green-700',
};

function delayColor(hours: number): string {
  if (hours < 24) return 'bg-green-50 text-green-700';
  if (hours < 48) return 'bg-yellow-50 text-yellow-700';
  return 'bg-red-50 text-red-700';
}

function formatStatus(s: string) {
  return s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// ==================== ASSIGN PO MODAL ====================

function AssignPoModal({
  mapping,
  onClose,
  onSaved,
}: {
  mapping: MappingItem;
  onClose: () => void;
  onSaved: (updated: MappingItem) => void;
}) {
  const [pos, setPos] = useState<PO[]>([]);
  const [search, setSearch] = useState('');
  const [selectedPoId, setSelectedPoId] = useState<string | null>(mapping.poId);
  const [status, setStatus] = useState<'unmapped' | 'in_progress' | 'mapped'>(mapping.status);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get('/purchase-orders?limit=100')
      .then(res => {
        const raw = res.data.data?.purchaseOrders ?? res.data.data?.items ?? res.data.data ?? [];
        setPos(raw);
      })
      .catch(() => {});
  }, []);

  const filtered = pos.filter(p =>
    p.number?.toLowerCase().includes(search.toLowerCase()) ||
    p.vendor?.name?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await api.patch(`/so-po-mapping/${mapping.id}`, {
        purchaseOrderId: selectedPoId,
        status,
      });
      const raw = res.data.data;
      const po = pos.find(p => p.id === selectedPoId);
      onSaved({
        ...mapping,
        status: raw.status,
        poId: raw.purchaseOrderId ?? null,
        poNumber: po?.number ?? mapping.poNumber,
      });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update mapping');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-2xl p-6 shadow-2xl"
        style={{ background: 'var(--bg-surface)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>Assign Purchase Order</h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{mapping.soNumber} — {mapping.itemDescription}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:opacity-70" style={{ background: 'var(--bg-muted)' }}>
            <X size={16} style={{ color: 'var(--text-secondary)' }} />
          </button>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}

        {/* Status */}
        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Mapping Status</label>
          <div className="flex gap-2">
            {(['unmapped', 'in_progress', 'mapped'] as const).map(s => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className="flex-1 rounded-lg border py-2 text-xs font-medium transition-all"
                style={{
                  borderColor: status === s ? 'var(--primary-600, #2563eb)' : 'var(--border-primary)',
                  background: status === s ? 'var(--primary-50, #eff6ff)' : 'var(--bg-surface)',
                  color: status === s ? 'var(--primary-700, #1d4ed8)' : 'var(--text-secondary)',
                }}
              >
                {formatStatus(s)}
              </button>
            ))}
          </div>
        </div>

        {/* PO Search */}
        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Link Purchase Order</label>
          <div className="relative mb-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by PO number or vendor..."
              className="w-full rounded-lg border py-2 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ink-0)]"
              style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
            />
          </div>

          {/* None option */}
          <button
            onClick={() => setSelectedPoId(null)}
            className="mb-1 w-full rounded-lg border px-3 py-2 text-left text-sm transition-all"
            style={{
              borderColor: selectedPoId === null ? 'var(--primary-600, #2563eb)' : 'var(--border-primary)',
              background: selectedPoId === null ? 'var(--primary-50, #eff6ff)' : 'var(--bg-surface)',
              color: 'var(--text-secondary)',
            }}
          >
            — No PO assigned
          </button>

          <div className="max-h-48 overflow-y-auto rounded-lg border" style={{ borderColor: 'var(--border-primary)' }}>
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs" style={{ color: 'var(--text-muted)' }}>No purchase orders found</p>
            ) : (
              filtered.map(po => (
                <button
                  key={po.id}
                  onClick={() => setSelectedPoId(po.id)}
                  className="flex w-full items-center justify-between border-b px-3 py-2.5 text-left text-sm transition-all last:border-b-0"
                  style={{
                    borderColor: 'var(--border-primary)',
                    background: selectedPoId === po.id ? 'var(--primary-50, #eff6ff)' : 'var(--bg-surface)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <div>
                    <span className="font-medium">{po.number}</span>
                    {po.vendor?.name && (
                      <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>{po.vendor.name}</span>
                    )}
                  </div>
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                    style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)' }}
                  >
                    {po.status}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
            style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== MAIN PAGE ====================

export function SoPoMappingPage() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState<KPIs | null>(null);
  const [items, setItems] = useState<MappingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [assignTarget, setAssignTarget] = useState<MappingItem | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [kpiRes, itemsRes] = await Promise.all([
        api.get('/so-po-mapping/kpis'),
        api.get('/so-po-mapping/items', {
          params: {
            page,
            ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
          },
        }),
      ]);
      const rawKpi = kpiRes.data.data;
      setKpis({
        avgMappingTimeHours: rawKpi.averageMappingTimeHours ?? rawKpi.avgMappingTimeHours ?? 0,
        unmapped: rawKpi.unmappedCount ?? rawKpi.unmapped ?? 0,
        inProgress: rawKpi.inProgressCount ?? rawKpi.inProgress ?? 0,
        mapped: rawKpi.mappedCount ?? rawKpi.mapped ?? 0,
        overdue: rawKpi.overdueCount ?? rawKpi.overdue ?? 0,
      });
      const rawItems = itemsRes.data.data.items || itemsRes.data.data || [];
      setItems(rawItems.map((m: any) => ({
        id: m.id,
        soId: m.salesOrderId ?? m.soId,
        soNumber: m.salesOrderNumber ?? m.soNumber ?? '—',
        itemDescription: m.itemDescription ?? m.description ?? '—',
        status: m.status,
        delayHours: m.mappingDelayHours ?? m.hoursSinceConfirmed ?? m.delayHours ?? 0,
        poId: m.purchaseOrderId ?? m.poId ?? null,
        poNumber: m.purchaseOrderNumber ?? m.poNumber ?? null,
        confirmedAt: m.soConfirmedAt ?? m.confirmedAt ?? null,
      })));
      setTotalPages(itemsRes.data.data.pagination?.totalPages ?? itemsRes.data.data.totalPages ?? 1);
    } catch {
      setError('Failed to load SO-PO mapping data');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleScan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const res = await api.post('/so-po-scanner/scan');
      const data = res.data.data;
      setScanResult(`Scan complete: ${data.newMappings ?? 0} new mappings found, ${data.updated ?? 0} updated`);
      fetchData();
    } catch {
      setScanResult('Scan failed. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const handleMappingUpdated = (updated: MappingItem) => {
    setItems(prev => prev.map(item => item.id === updated.id ? updated : item));
    // Refresh KPIs
    api.get('/so-po-mapping/kpis').then(res => {
      const rawKpi = res.data.data;
      setKpis({
        avgMappingTimeHours: rawKpi.averageMappingTimeHours ?? 0,
        unmapped: rawKpi.unmappedCount ?? 0,
        inProgress: rawKpi.inProgressCount ?? 0,
        mapped: rawKpi.mappedCount ?? 0,
        overdue: rawKpi.overdueCount ?? 0,
      });
    }).catch(() => {});
  };

  return (
    <div className="mx-auto max-w-6xl">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
            SO-PO Mapping
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Track and manually assign purchase orders to sales order items
          </p>
        </div>
        <button
          onClick={handleScan}
          disabled={scanning}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors disabled:opacity-50"
        >
          {scanning ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Run Scan
        </button>
      </div>

      {/* Scan Result */}
      {scanResult && (
        <div
          className="mb-4 flex items-center justify-between rounded-lg border px-4 py-3 text-sm"
          style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
        >
          <span>{scanResult}</span>
          <button onClick={() => setScanResult(null)} className="ml-4 text-xs hover:underline" style={{ color: 'var(--text-muted)' }}>Dismiss</button>
        </div>
      )}

      {/* KPI Cards */}
      {kpis && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
          <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
            <div className="mb-2 flex items-center gap-2">
              <Clock size={14} style={{ color: 'var(--text-muted)' }} />
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Avg Time</span>
            </div>
            <p className={`text-2xl font-bold ${(kpis.avgMappingTimeHours ?? 0) < 24 ? 'text-green-600' : (kpis.avgMappingTimeHours ?? 0) < 48 ? 'text-yellow-600' : 'text-red-600'}`}>
              {(kpis.avgMappingTimeHours ?? 0).toFixed(1)}h
            </p>
          </div>
          {[
            { label: 'Unmapped', value: kpis.unmapped, badgeClass: 'bg-red-100 text-red-700', icon: AlertTriangle },
            { label: 'In Progress', value: kpis.inProgress, badgeClass: 'bg-blue-100 text-blue-700', icon: Loader2 },
            { label: 'Mapped', value: kpis.mapped, badgeClass: 'bg-green-100 text-green-700', icon: CheckCircle2 },
            { label: 'Overdue >48h', value: kpis.overdue, badgeClass: 'bg-red-100 text-red-700', icon: AlertTriangle },
          ].map(({ label, value, badgeClass, icon: Icon }) => (
            <div key={label} className="rounded-xl border p-5" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
              <div className="mb-2 flex items-center gap-2">
                <Icon size={14} style={{ color: 'var(--text-muted)' }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{label}</span>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{value}</p>
                {value > 0 && <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass}`}>{value}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="mb-4 flex items-center gap-3">
        <Filter size={14} style={{ color: 'var(--text-muted)' }} />
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-lg border px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
        >
          <option value="all">All Statuses</option>
          <option value="unmapped">Unmapped</option>
          <option value="in_progress">In Progress</option>
          <option value="mapped">Mapped</option>
        </select>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Items Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          <span className="ml-2 text-sm" style={{ color: 'var(--text-muted)' }}>Loading...</span>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-primary)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="border-b text-left text-xs font-medium uppercase tracking-wider"
                  style={{ backgroundColor: 'var(--bg-inset)', borderColor: 'var(--border-primary)', color: 'var(--text-muted)' }}
                >
                  <th className="px-4 py-3">SO Number</th>
                  <th className="px-4 py-3">Item</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Delay</th>
                  <th className="px-4 py-3">Linked PO</th>
                  <th className="px-4 py-3">Confirmed At</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center" style={{ color: 'var(--text-muted)' }}>
                      <Link2 size={32} className="mx-auto mb-2 opacity-40" />
                      <p>No mapping items found</p>
                      <p className="text-xs mt-1">Confirm a sales order to generate SO-PO mapping items.</p>
                    </td>
                  </tr>
                ) : (
                  items.map(item => (
                    <tr
                      key={item.id}
                      className="transition-colors"
                      style={{ backgroundColor: 'var(--bg-surface)' }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--bg-inset)')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'var(--bg-surface)')}
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/sales-orders/${item.soId}`)}
                          className="font-medium text-[var(--ink-0)] hover:underline"
                        >
                          {item.soNumber}
                        </button>
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>{item.itemDescription}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[item.status] || ''}`}>
                          {formatStatus(item.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${delayColor(item.delayHours ?? 0)}`}>
                          {(item.delayHours ?? 0).toFixed(1)}h
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {item.poId && item.poNumber ? (
                          <button
                            onClick={() => navigate(`/purchase-orders/${item.poId}`)}
                            className="font-medium text-[var(--ink-0)] hover:underline"
                          >
                            {item.poNumber}
                          </button>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>
                        {item.confirmedAt ? formatDate(item.confirmedAt) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setAssignTarget(item)}
                            className="rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-[var(--saffron-50)] hover:border-[var(--ink-3)] hover:text-[var(--ink-0)]"
                            style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
                          >
                            {item.status === 'mapped' ? 'Edit' : 'Assign PO'}
                          </button>
                          <button
                            onClick={() => navigate(`/sales-orders/${item.soId}`)}
                            className="rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors"
                            style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}
                          >
                            View SO
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div
              className="flex items-center justify-between border-t px-4 py-3"
              style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-inset)' }}
            >
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Page {page} of {totalPages}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-lg border p-1.5 transition-colors hover:bg-gray-100 disabled:opacity-40"
                  style={{ borderColor: 'var(--border-primary)' }}
                >
                  <ChevronLeft size={14} style={{ color: 'var(--text-primary)' }} />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="rounded-lg border p-1.5 transition-colors hover:bg-gray-100 disabled:opacity-40"
                  style={{ borderColor: 'var(--border-primary)' }}
                >
                  <ChevronRight size={14} style={{ color: 'var(--text-primary)' }} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Assign PO Modal */}
      {assignTarget && (
        <AssignPoModal
          mapping={assignTarget}
          onClose={() => setAssignTarget(null)}
          onSaved={updated => {
            handleMappingUpdated(updated);
            setAssignTarget(null);
          }}
        />
      )}
    </div>
  );
}
